import {
  communityAdminSessionResponseSchema,
  type CommunityAdminSession,
} from "@squeek/module-contracts/community-admin-v1";
import { errorMessage, isAdminAuthLoss, type AdminApiError, type CommunityAdminApi } from "./api";
import type { ZodType } from "zod";

export const ADMIN_SESSION_KEY = "squeek.community.admin.session";
type SessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type AuthApi<S extends CommunityAdminSession> = Pick<CommunityAdminApi, "challenge" | "logout"> & {
  verify: (request: Parameters<CommunityAdminApi["verify"]>[0]) => Promise<S & { token: string }>;
  session: () => Promise<S>;
};
export interface AdminAuthState<S extends CommunityAdminSession = CommunityAdminSession> {
  readonly status: "signed-out" | "restoring" | "signing" | "authenticated" | "unavailable" | "logging-out";
  readonly session: S | null;
  readonly error: string | null;
}

/** Scope-specific tab storage. Saved state never suffices to open the private UI. */
export class AdminSessionStore<S extends CommunityAdminSession = CommunityAdminSession> {
  private readonly listeners = new Set<() => void>();
  private saved: (S & { token: string }) | null = null;
  private state: AdminAuthState<S> = { status: "signed-out", session: null, error: null };
  private revision = 0;
  private wallet: string | undefined;
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingLogout = false;
  private restoring: Promise<void> | null = null;

  constructor(
    private readonly storage: SessionStorage | null,
    private readonly clearPrivateData: () => void,
    private readonly now: () => number = Date.now,
    private readonly schedule: typeof setTimeout = globalThis.setTimeout.bind(globalThis),
    private readonly unschedule: typeof clearTimeout = globalThis.clearTimeout.bind(globalThis),
    private readonly key = ADMIN_SESSION_KEY,
    private readonly schema: ZodType<S & { token: string }, unknown> = communityAdminSessionResponseSchema as unknown as ZodType<S & { token: string }, unknown>,
    private readonly label = "admin",
  ) {
    try {
      const raw = storage?.getItem(this.key);
      if (raw) {
        const parsed = this.schema.safeParse(JSON.parse(raw));
        if (parsed.success && Date.parse(parsed.data.expiresAt) > now()) {
          this.saved = parsed.data;
          this.state = { status: "restoring", session: null, error: null };
          this.armExpiry();
        } else storage?.removeItem(this.key);
      }
    } catch {
      // Unavailable tab storage keeps this login memory-only.
    }
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  readonly snapshot = (): AdminAuthState<S> => this.state;
  readonly token = (): string | null => {
    if (this.saved && Date.parse(this.saved.expiresAt) <= this.now()) this.clear(`Your ${this.label} session expired. Sign in again.`);
    return this.saved?.token ?? null;
  };

  private publish(state: AdminAuthState<S>): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }

  private persist(): void {
    try {
      if (this.saved) this.storage?.setItem(this.key, JSON.stringify(this.saved));
      else this.storage?.removeItem(this.key);
    } catch {
      // Session storage may be disabled; the in-memory session remains usable.
    }
  }

  private armExpiry(): void {
    if (this.expiryTimer !== undefined) this.unschedule(this.expiryTimer);
    this.expiryTimer = undefined;
    if (this.saved) {
      this.expiryTimer = this.schedule(() => this.clear(`Your ${this.label} session expired. Sign in again.`),
        Math.max(0, Date.parse(this.saved.expiresAt) - this.now()));
    }
  }

  clear(error: string | null = null): void {
    this.revision += 1;
    this.saved = null;
    this.pendingLogout = false;
    this.persist();
    this.armExpiry();
    this.clearPrivateData();
    this.publish({ status: "signed-out", session: null, error });
  }

  readonly onAuthLoss = (token: string, error: AdminApiError): void => {
    if (this.saved?.token === token) this.clear(error.status !== 401
      ? `This wallet and linked X account no longer have ${this.label} permission.`
      : `Your ${this.label} session is no longer valid. Sign in again.`);
  };

  /** A connector account change invalidates even an in-flight signature or restore. */
  setWallet(address: string | undefined): void {
    const wallet = address?.toLowerCase();
    if (wallet === this.wallet) return;
    const previous = this.wallet;
    this.wallet = wallet;
    if ((previous !== undefined && previous !== wallet)
      || (wallet !== undefined && this.saved && this.saved.identity.walletAddress.toLowerCase() !== wallet)) {
      this.clear("Wallet changed. Sign in with the approved wallet to continue.");
    }
  }

  restore(api: AuthApi<S>): Promise<void> {
    if (this.restoring) return this.restoring;
    if (!this.token()) return Promise.resolve();
    const revision = this.revision;
    const confirmed = this.state.status === "authenticated" ? this.state.session : null;
    if (confirmed === null) this.publish({ status: "restoring", session: null, error: null });
    this.restoring = (async () => {
      try {
        const session = await api.session();
        if (revision !== this.revision || !this.saved) return;
        if (session.identity.walletAddress !== this.saved.identity.walletAddress
          || session.identity.xUserId !== this.saved.identity.xUserId
          || session.identity.personId !== this.saved.identity.personId) {
          this.clear(`The ${this.label} session identity changed. Sign in again.`);
          return;
        }
        this.saved = { ...session, token: this.saved.token };
        if (!this.token()) return;
        this.persist();
        this.armExpiry();
        this.publish({ status: "authenticated", session, error: null });
      } catch (error) {
        if (revision !== this.revision) return;
        if (isAdminAuthLoss(error)) {
          this.clear(errorMessage(error));
        } else this.publish(confirmed === null
          ? { status: "unavailable", session: null, error: errorMessage(error) }
          : { status: "authenticated", session: confirmed, error: errorMessage(error) });
      } finally {
        this.restoring = null;
      }
    })();
    return this.restoring;
  }

  async signIn(api: AuthApi<S>, address: string, chainId: number,
    sign: (message: string) => Promise<`0x${string}`>): Promise<void> {
    if (this.state.status === "signing") return;
    this.clear();
    const revision = this.revision;
    this.publish({ status: "signing", session: null, error: null });
    try {
      const challenge = await api.challenge({ address, chainId });
      if (revision !== this.revision) return;
      const signature = await sign(challenge.message);
      if (revision !== this.revision) return;
      const session = await api.verify({ nonce: challenge.nonce, signature });
      if (revision !== this.revision) return;
      if (session.identity.walletAddress.toLowerCase() !== address.toLowerCase()) {
        throw new Error(`The signed wallet does not match the ${this.label} session.`);
      }
      if (Date.parse(session.expiresAt) <= this.now()) throw new Error(`The ${this.label} session has expired. Sign in again.`);
      this.saved = session;
      this.persist();
      this.armExpiry();
      const authenticated = { ...session };
      Reflect.deleteProperty(authenticated, "token");
      this.publish({ status: "authenticated", session: authenticated, error: null });
    } catch (error) {
      if (revision === this.revision) this.clear(errorMessage(error));
    }
  }

  async logout(api: AuthApi<S>): Promise<void> {
    if (!this.token()) return;
    const revision = ++this.revision;
    this.pendingLogout = true;
    this.clearPrivateData();
    this.publish({ status: "logging-out", session: null, error: null });
    try {
      await api.logout();
      if (revision === this.revision) this.clear();
    } catch (error) {
      if (revision !== this.revision) return;
      if (isAdminAuthLoss(error)) this.clear();
      else this.publish({ status: "unavailable", session: null, error: `Logout could not be confirmed. ${errorMessage(error)}` });
    }
  }

  retry(api: AuthApi<S>): Promise<void> {
    return this.pendingLogout ? this.logout(api) : this.restore(api);
  }
}
